import { ComponentEntity, SystemEntity } from "@backstage/catalog-model";
import { EmptyState } from "@backstage/core-components";
import { useEntity } from "@backstage/plugin-catalog-react";
import { MessagingResource } from "@internal/open-catalog";
import { AppBar, createStyles, makeStyles, Tab, Tabs, Theme } from '@material-ui/core';
import React from 'react';
import { SystemMessagingMonitoringPage } from "./SystemMessagingMonitoringPage";
import { SystemMessagingTopologyEditPage } from "./SystemMessagingTopologyEditPage";
import { SystemMessagingTopologyPage } from "./SystemMessagingTopologyPage";
import { RequirePermission } from "@backstage/plugin-permission-react";
import { amqpTopologyReadDraftPermission, amqpTopologySubmitDraftPermission } from "@internal/backstage-plugin-amqp-topology-manager-common";
import useAsync from 'react-use/esm/useAsync';
import { microsoftAuthApiRef, useApi } from "@backstage/core-plugin-api";

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {

  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      {children}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    complementaryAppBar: {
      zIndex: 1009,
      marginBottom: theme.spacing(2)
    },
    tabs: {
      '& > div > span': {
        height: 4
      }
    }
  })
);

export const SystemMessagingPage = (props: { system: SystemEntity, components: ComponentEntity[], resources: MessagingResource[], resourcesReload: () => Promise<MessagingResource[]> }) => {

  const classes = useStyles();
  const authApi = useApi(microsoftAuthApiRef);

  const { components, resources, resourcesReload, system } = props;

  const { value: loggedUserIsSystemOwner } = useAsync(async () => {
    const ownerGroup = system.relations?.find(relation => relation.type === 'ownedBy');
    if(!ownerGroup)
      return null;

    const identity = await authApi.getBackstageIdentity();
    return identity?.identity.ownershipEntityRefs?.some((entityRef) => entityRef === ownerGroup.targetRef);
  }, [authApi]);

  const { entity } = useEntity();
  const services = components
    .filter(component => component.spec.type === 'service')
    .filter(svc => svc.metadata?.annotations?.['open.totvs.io/messaging-channel-name']) || [];

  const [value, setValue] = React.useState(0);
  const handleChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  if (!resources || resources.length === 0 || !services || services.length === 0) {
    return (<EmptyState missing="content" title="The system has no services with messaging enabled." />);
  }

  return (<>
    <AppBar position="sticky" role="complementary" className={classes.complementaryAppBar} >
      <Tabs className={classes.tabs}
        value={value}
        onChange={handleChange}
        variant="fullWidth"
        centered
      >
        <Tab label="MONITORING" />
        <Tab label="TOPOLOGY" />
        <Tab label="CHANGE REQUEST" disabled={!loggedUserIsSystemOwner} />
      </Tabs>
    </AppBar>

    <TabPanel value={value} index={0}>
      {value === 0 && <SystemMessagingMonitoringPage resources={resources} system={system} />}
    </TabPanel>

    <TabPanel value={value} index={1}>
      <RequirePermission permission={amqpTopologyReadDraftPermission}>
        {value === 1 && <SystemMessagingTopologyPage services={services} resources={resources} />}
      </RequirePermission>
    </TabPanel>

    <TabPanel value={value} index={2}>
      <RequirePermission permission={amqpTopologySubmitDraftPermission}>
        {value === 2 && <SystemMessagingTopologyEditPage services={services} entity={entity as ComponentEntity} reloadResources={resourcesReload} />}
      </RequirePermission>
    </TabPanel>

  </>);
}
